/* Shop the feed — behaviour of the pop-ups in sections/shop-the-feed (blocks/_feed-item).
   Opening, closing, focus return, one-at-a-time and the theme editor are the overlay system's
   (assets/overlay.js: a post card is a [data-overlay-open] trigger, its <li> carries
   data-overlay-select). This module only adds, by delegation so editor re-renders need no
   re-binding:
   - Previous / Next: close this post's pop-up and open the neighbouring one in the same section
     without replaying the entrance; focus returns to that post's card on close. Disabled at the
     ends, hidden when the section has one post.
   - Featured products: the thumbnails switch which compact card is shown (aria-pressed).
   - Media slider: arrows and bullets drive a native scroll-snap row; the bullets follow swipes.
   - Video: plays muted when its slide is showing in an open pop-up (native controls pause it;
     never under reduced motion), pauses when the slide or the pop-up goes away. */

const POP = 'dialog.sf-pop';
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const popsOf = (dialog) => {
  const scope = dialog.closest('.shopify-section') || document;
  return [...scope.querySelectorAll(POP)];
};

const triggerOf = (dialog) => document.querySelector(`[data-overlay-open="${CSS.escape(dialog.id)}"]`);

/* ---------- Previous / Next ---------- */
function syncSteps(dialog) {
  const pops = popsOf(dialog);
  const index = pops.indexOf(dialog);
  dialog.toggleAttribute('data-feed-single', pops.length < 2);
  const prev = dialog.querySelector('[data-feed-step="-1"]');
  const next = dialog.querySelector('[data-feed-step="1"]');
  if (prev) prev.disabled = index <= 0;
  if (next) next.disabled = index === -1 || index >= pops.length - 1;
}

function step(dialog, direction) {
  const pops = popsOf(dialog);
  const target = pops[pops.indexOf(dialog) + direction];
  const overlay = window.Munkwell?.overlay;
  if (!target || !overlay) return;
  // The pop-up is replaced in place: no second entrance, and closing it later returns focus
  // to the card of the post that is showing.
  overlay.open(target.id, triggerOf(target), { instant: true });
  const button = target.querySelector(`[data-feed-step="${direction}"]:not(:disabled)`) || target.querySelector('[data-feed-step]:not(:disabled)');
  button?.focus({ preventScroll: true });
}

/* ---------- Featured product switcher ---------- */
function showProduct(tag) {
  const group = tag.closest('[data-feed-products]');
  if (!group) return;
  const index = tag.getAttribute('data-feed-product');
  group.querySelectorAll('[data-feed-product]').forEach((t) => t.setAttribute('aria-pressed', String(t === tag)));
  group.querySelectorAll('[data-feed-card]').forEach((card) => card.classList.toggle('is-active', card.getAttribute('data-feed-card') === index));
}

/* ---------- Media slider ---------- */
const slidesOf = (slider) => [...slider.querySelectorAll(':scope > [data-feed-slides] > .sf-pop__slide')];

function currentSlide(slider) {
  const row = slider.querySelector('[data-feed-slides]');
  if (!row || !row.clientWidth) return 0;
  const offset = Math.abs(row.scrollLeft); // RTL scrolls negative
  return Math.round(offset / row.clientWidth);
}

function goToSlide(slider, index) {
  const row = slider.querySelector('[data-feed-slides]');
  const slides = slidesOf(slider);
  const clamped = Math.max(0, Math.min(slides.length - 1, index));
  const slide = slides[clamped];
  if (!row || !slide) return;
  const rtl = getComputedStyle(row).direction === 'rtl';
  row.scrollTo({ left: (rtl ? -1 : 1) * clamped * row.clientWidth, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
}

function syncSlider(slider) {
  const slides = slidesOf(slider);
  const index = currentSlide(slider);
  slider.querySelectorAll('[data-feed-dot]').forEach((dot) => {
    if (Number(dot.getAttribute('data-feed-dot')) === index) dot.setAttribute('aria-current', 'true');
    else dot.removeAttribute('aria-current');
  });
  const prev = slider.querySelector('[data-feed-slide="-1"]');
  const next = slider.querySelector('[data-feed-slide="1"]');
  if (prev) prev.disabled = index <= 0;
  if (next) next.disabled = index >= slides.length - 1;
  syncVideos(slider.closest(POP));
}

/* ---------- Video ---------- */
function syncVideos(dialog) {
  if (!dialog) return;
  const slider = dialog.querySelector('[data-feed-slider]');
  const index = slider ? currentSlide(slider) : 0;
  const slides = slider ? slidesOf(slider) : [];
  slides.forEach((slide, i) => {
    const video = slide.querySelector('video');
    if (!video) return;
    const showing = dialog.open && i === index;
    if (showing && !reducedMotion.matches && !video.dataset.feedUserPaused) {
      video.play()?.catch(() => {});
    } else if (!showing) {
      video.pause();
    }
  });
}

/* ---------- Wiring ---------- */
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const stepButton = target.closest('[data-feed-step]');
  if (stepButton) {
    const dialog = stepButton.closest(POP);
    if (dialog && !stepButton.disabled) step(dialog, Number(stepButton.getAttribute('data-feed-step')));
    return;
  }

  const tag = target.closest('[data-feed-product]');
  if (tag) {
    showProduct(tag);
    return;
  }

  const arrow = target.closest('[data-feed-slide]');
  if (arrow) {
    const slider = arrow.closest('[data-feed-slider]');
    if (slider) goToSlide(slider, currentSlide(slider) + Number(arrow.getAttribute('data-feed-slide')));
    return;
  }

  const dot = target.closest('[data-feed-dot]');
  if (dot) {
    const slider = dot.closest('[data-feed-slider]');
    if (slider) goToSlide(slider, Number(dot.getAttribute('data-feed-dot')));
  }
});

// Scroll doesn't bubble; a capturing listener sees every slide row.
let scrollFrame = 0;
document.addEventListener(
  'scroll',
  (event) => {
    const row = event.target instanceof Element ? event.target : null;
    if (!row?.matches('[data-feed-slides]')) return;
    cancelAnimationFrame(scrollFrame);
    scrollFrame = requestAnimationFrame(() => {
      const slider = row.closest('[data-feed-slider]');
      if (slider) syncSlider(slider);
    });
  },
  true
);

// Remember a pause the shopper made with the native controls, so stepping slides doesn't undo it.
document.addEventListener(
  'pause',
  (event) => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement) || !video.closest(POP)) return;
    const dialog = video.closest(POP);
    if (dialog.open && !document.hidden && video.isConnected && video.closest('.sf-pop__slide') && !video.ended) {
      // A pause while the slide shows and the dialog is open came from the shopper.
      const slider = dialog.querySelector('[data-feed-slider]');
      const slides = slider ? slidesOf(slider) : [];
      if (slides[currentSlide(slider)]?.contains(video)) video.dataset.feedUserPaused = '1';
    }
  },
  true
);
document.addEventListener(
  'play',
  (event) => {
    const video = event.target;
    if (video instanceof HTMLVideoElement) delete video.dataset.feedUserPaused;
  },
  true
);

document.addEventListener('mw:overlay:open', (event) => {
  const dialog = event.target instanceof Element ? event.target.closest(POP) : null;
  if (!dialog) return;
  syncSteps(dialog);
  const slider = dialog.querySelector('[data-feed-slider]');
  if (slider) syncSlider(slider);
  else syncVideos(dialog);
});

document.addEventListener('mw:overlay:close', (event) => {
  const dialog = event.target instanceof Element ? event.target.closest(POP) : null;
  if (!dialog) return;
  dialog.querySelectorAll('video').forEach((video) => {
    video.pause();
    delete video.dataset.feedUserPaused;
  });
});

// Step buttons are right before the first open, too (theme editor opens without a click).
const syncAll = () => document.querySelectorAll(POP).forEach(syncSteps);
syncAll();
document.addEventListener('shopify:section:load', syncAll);
